import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Input, Select, Space } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

const DEBOUNCE_MS = 350;

/**
 * Debounced toolbar search. `value` is the committed filter value; typing updates a local draft
 * and commits after a short pause, and an outside change (column search, reset) refreshes the draft.
 * Pass `fields` to show a "search by" picker for backends that can only match one field at a time.
 */
function TableSearchInput({ value, onChange, placeholder, fields, field, onFieldChange, className, size }) {
    const { t } = useTranslation("table");
    const [draft, setDraft] = useState(value ?? "");
    const committedRef = useRef(value ?? "");
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const inputRef = useRef(null);

    // "/" jumps to the table search, like most admin tools. Only the visible input reacts,
    // and only when the user isn't already typing somewhere.
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key !== "/" || event.ctrlKey || event.metaKey || event.altKey) return;
            const active = document.activeElement;
            const typing =
                active && (["INPUT", "TEXTAREA", "SELECT"].includes(active.tagName) || active.isContentEditable);
            const element = inputRef.current?.input;
            if (typing || !element || element.offsetParent === null) return;
            event.preventDefault();
            element.focus();
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, []);

    useEffect(() => {
        const next = value ?? "";
        if (next !== committedRef.current) {
            committedRef.current = next;
            setDraft(next);
        }
    }, [value]);

    useEffect(() => {
        const trimmed = draft.trim();
        if (trimmed === committedRef.current) return undefined;
        const timer = setTimeout(() => {
            committedRef.current = trimmed;
            onChangeRef.current(trimmed);
        }, DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [draft]);

    const commitNow = () => {
        const trimmed = draft.trim();
        if (trimmed === committedRef.current) return;
        committedRef.current = trimmed;
        onChange(trimmed);
    };

    const input = (
        <Input
            ref={inputRef}
            allowClear
            size={size}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onPressEnter={commitNow}
            onKeyDown={(e) => {
                if (e.key === "Escape" && draft) {
                    e.stopPropagation();
                    setDraft("");
                }
            }}
            prefix={<SearchOutlined className="text-gray-400" />}
            suffix={
                draft ? null : (
                    <kbd
                        title={t("searchShortcut")}
                        className="hidden rounded border border-gray-200 bg-gray-50 px-1.5 text-[11px] leading-4 text-gray-400 lg:inline">
                        /
                    </kbd>
                )
            }
            placeholder={placeholder || t("searchPlaceholder")}
            aria-label={placeholder || t("search")}
            className="min-w-0"
        />
    );

    if (!fields?.length) {
        return <div className={className ?? "w-full sm:w-80"}>{input}</div>;
    }

    return (
        <Space.Compact className={className ?? "w-full sm:w-[26rem]"}>
            <Select
                size={size}
                value={field}
                onChange={onFieldChange}
                options={fields.map((option) => ({ value: option.key, label: option.label }))}
                popupMatchSelectWidth={false}
                aria-label={t("searchBy")}
                className="shrink-0"
                style={{ width: 140 }}
            />
            {input}
        </Space.Compact>
    );
}

TableSearchInput.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    placeholder: PropTypes.string,
    fields: PropTypes.arrayOf(PropTypes.shape({ key: PropTypes.string.isRequired, label: PropTypes.node.isRequired })),
    field: PropTypes.string,
    onFieldChange: PropTypes.func,
    className: PropTypes.string,
    size: PropTypes.oneOf(["small", "middle", "large"]),
};

export default TableSearchInput;
